import { useCallback } from 'react'

import { useUnit } from 'effector-react'
import { useMutation } from 'react-query'

import { useToast } from '@/components/ui/toast.tsx'
import { $fanId, toggleMute } from '@/models/chattingUI/chats/chats.ts'
import { $creatorId } from '@/models/creators.ts'
import { chatsService } from '@/modules/chatting/service/chats.ts'
import type {
  CommonChatModificationParams,
  MuteChatParams,
} from '@/modules/chatting/types.ts'
import { handleError } from '@/utils/error'

interface MuteParams {
  onMuteSuccess?: () => void
  onUnmuteSuccess?: () => void
}

export function useToggleMuteChat(params?: MuteParams) {
  const toast = useToast()
  const creatorId = useUnit($creatorId)
  const fanId = useUnit($fanId)

  const muteChatMutation = useMutation(
    (params: MuteChatParams) => chatsService.muteChat(params),
    {
      onSuccess: (_data, { fanId }) => {
        toast.success('Successfully muted fan')
        toggleMute({
          fanId,
          isMutedNotifications: true,
        })

        params?.onMuteSuccess?.()
      },
      onError: (error) => {
        handleError(
          error,
          'Unexpected error occurred while muting chat, please try again later',
        )
      },
    },
  )

  const muteChat = useCallback(
    async (unmuteAt: string) => {
      if (fanId) {
        await muteChatMutation.mutateAsync({
          creatorId: creatorId!,
          fanId,
          unmuteAt,
        })
      }
    },
    [fanId, creatorId, muteChatMutation],
  )

  const unmuteChatMutation = useMutation(
    (params: CommonChatModificationParams) => chatsService.unmuteChat(params),
    {
      onSuccess: (_data, { fanId }) => {
        toast.success('Successfully unmuted fan')
        toggleMute({
          fanId,
          isMutedNotifications: false,
        })
        params?.onUnmuteSuccess?.()
      },
      onError: (error) => {
        handleError(
          error,
          'Unexpected error occurred while unmuting chat, please try again later',
        )
      },
    },
  )

  const unmuteChat = useCallback(() => {
    if (fanId) {
      unmuteChatMutation.mutate({
        creatorId: creatorId!,
        fanId,
      })
    }
  }, [fanId, creatorId, unmuteChatMutation])

  return {
    muteChat,
    unmuteChat,
    isMuteChatLoading: muteChatMutation.isLoading,
    isUnmuteChatLoading: unmuteChatMutation.isLoading,
  }
}
